import { AgencySignupPayload, AgencySignupSource, MdtPurchaseWindow } from '@clipr/lib';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { ApiService } from '../../api/apiService';
import { resolveAgencySignupError } from './agencySignupErrors';
import {
  AGENCY_SIGNUP_DRAFT_KEY,
  AgencyOfficerFormRow,
  AgencySignupFormModel,
  createAgencySignupFormModel,
  createEmptyOfficerRow,
  getRosterLimits,
  isOfficerRowComplete,
  MAX_PILOT_OFFICERS,
} from './agencySignupSchema';
import { parseAgencyRosterCsv } from './agencyCsv';

type AgencySignupStore = {
  apiService: Pick<ApiService, 'saveAgencySignupDraft' | 'resumeAgencySignupDraft' | 'submitAgencySignup'>;
};

type DraftPayload = Omit<AgencySignupPayload, 'sponsorPassword' | 'swornOfficersCount' | 'officers'> & {
  swornOfficersCount: number;
  officers: AgencyOfficerFormRow[];
};

const SAVE_DRAFT_DELAY_MS = 3000;

export class AgencySignupPageState {

  constructor(readonly store: AgencySignupStore) {
    makeObservable(this);
  }

  @observable form: AgencySignupFormModel = createAgencySignupFormModel();
  @observable isSavingDraft = false;
  @observable isSubmitting = false;
  @observable isResuming = false;
  @observable saveStatus: string | null = null;
  @observable error: string | null = null;
  @observable rosterCsvError: string | null = null;
  @observable rosterCsvStatus: string | null = null;
  @observable submitResult: { orgId: string; sponsorUserId: string; agencyTemplateId: string } | null = null;

  private saveTimer: ReturnType<typeof setTimeout> | null = null;

  @computed
  get swornOfficerCount(): number {
    return Number(this.form.swornOfficersCount) || 0;
  }

  @computed
  get agencyDomain(): string {
    return this.form.sponsorEmail.trim().toLowerCase().split('@')[1] ?? '';
  }

  @computed
  get pilotRosterLimit(): number {
    return getRosterLimits(this.swornOfficerCount).pilotRows;
  }

  @computed
  get isAccountValid(): boolean {
    return Boolean(
      this.form.sponsorFirstName.trim() &&
      this.form.sponsorLastName.trim() &&
      this.form.sponsorTitle.trim() &&
      this.form.sponsorEmail.trim() &&
      this.form.sponsorPassword.trim() &&
      this.form.agencyName.trim() &&
      this.agencyDomain &&
      this.swornOfficerCount > 0);
  }

  @computed
  get areRequiredRowsComplete(): boolean {
    return this.form.officers.length > 0 && this.form.officers.every(isOfficerRowComplete);
  }

  @computed
  get canSubmit(): boolean {
    return this.isAccountValid &&
      this.areRequiredRowsComplete &&
      this.form.acceptedTerms &&
      !this.isSubmitting;
  }

  @action
  mounted() {
    this.hydrateFromLocalStorage();
  }

  @action
  unmounted() {
    this.clearSaveTimer();
  }

  @action
  resetForm() {
    this.clearSaveTimer();
    this.form = createAgencySignupFormModel();
    this.error = null;
    this.saveStatus = null;
    localStorage.removeItem(AGENCY_SIGNUP_DRAFT_KEY);
  }

  @action
  updateField<TKey extends keyof AgencySignupFormModel>(key: TKey, value: AgencySignupFormModel[TKey]) {
    this.form[key] = value;
    this.saveDraftDebounced();
  }

  @action
  updateOfficer(index: number, key: keyof AgencyOfficerFormRow, value: string) {
    const row = this.form.officers[index];
    if (!row)
      return;

    row[key] = isEmailKey(key) && value.endsWith('@') && !row[key].includes('@') ?
      value + this.agencyDomain :
      value;
    this.saveDraftDebounced();
  }

  @action
  generateRoster() {
    this.form.officers = resizeRows(this.form.officers, this.pilotRosterLimit);
  }

  @action
  importRosterCsv(data: string[][]) {
    const result = parseAgencyRosterCsv(data);
    this.rosterCsvError = result.errors.length > 0 ?
      result.errors.slice(0, 8).join('\n') :
      null;

    if (result.rows.length === 0) {
      this.rosterCsvStatus = null;
      if (!this.rosterCsvError)
        this.rosterCsvError = 'No roster rows were found in the CSV.';
      return;
    }

    const officers = result.rows;
    if (!this.validateRosterCsvImport(officers.length))
      return;

    this.form.officers = officers;
    this.form.swornOfficersCount = String(Math.max(
      this.swornOfficerCount,
      this.form.officers.length,
    ));
    this.rosterCsvStatus = `${officers.length} pilot roster ${getRosterRowLabel(officers.length)} imported.`;
    this.saveDraftDebounced();
  }

  private validateRosterCsvImport(rowCount: number): boolean {
    if (rowCount > MAX_PILOT_OFFICERS) {
      this.rosterCsvStatus = null;
      this.rosterCsvError = `Pilot roster CSV can include up to ${MAX_PILOT_OFFICERS} officers.`;
      return false;
    }

    return true;
  }

  @action
  saveDraftDebounced() {
    this.clearSaveTimer();
    this.saveStatus = 'Draft pending';
    this.persistLocalDraft();
    this.saveTimer = setTimeout(() => {
      void this.saveDraft();
    }, SAVE_DRAFT_DELAY_MS);
  }

  @action
  async saveDraft() {
    if (!this.form.sponsorEmail.trim())
      return;

    this.clearSaveTimer();
    this.isSavingDraft = true;

    const [res, err] = await this.store.apiService.saveAgencySignupDraft({
      email: this.form.sponsorEmail.trim(),
      payload: JSON.stringify(this.toApiPayload('draft')),
    });

    runInAction(() => {
      this.isSavingDraft = false;
      if (err) {
        this.saveStatus = 'Draft save failed — your progress is kept locally';
        return;
      }

      this.saveStatus = res?.saveAgencySignupDraft.updatedAt ?
        `Draft saved ${new Date(res.saveAgencySignupDraft.updatedAt).toLocaleTimeString()}` :
        'Draft saved';
    });
  }

  @action
  async resumeFromQuery(search: string) {
    const query = new URLSearchParams(search);
    const draftId = query.get('draft');
    const token = query.get('token');

    if (!draftId || !token) {
      this.error = 'Resume link is missing draft or token.';
      return;
    }

    this.isResuming = true;
    this.error = null;

    const [res, err] = await this.store.apiService.resumeAgencySignupDraft({ draftId, token });

    runInAction(() => {
      this.isResuming = false;
      if (err) {
        this.error = resolveAgencySignupError(err);
        return;
      }

      this.hydrateFromPayload(res?.resumeAgencySignupDraft.payload);
      this.saveStatus = 'Draft resumed';
    });
  }

  @action
  async submit(source?: AgencySignupSource) {
    if (!this.canSubmit)
      return;

    this.isSubmitting = true;
    this.error = null;

    const [res, err] = await this.store.apiService.submitAgencySignup({
      input: this.toApiPayload('submit', source),
    });

    runInAction(() => {
      this.isSubmitting = false;
      if (err) {
        this.error = resolveAgencySignupError(err);
        return;
      }

      this.submitResult = res?.submitAgencySignup ?? null;
      this.saveStatus = null;
      localStorage.removeItem(AGENCY_SIGNUP_DRAFT_KEY);
    });
  }

  toApiPayload(mode: 'draft'): DraftPayload;
  toApiPayload(mode: 'submit', source?: AgencySignupSource): AgencySignupPayload;
  toApiPayload(mode: 'draft' | 'submit', source?: AgencySignupSource): DraftPayload | AgencySignupPayload {
    const base = {
      sponsorFirstName: this.form.sponsorFirstName.trim(),
      sponsorLastName: this.form.sponsorLastName.trim(),
      sponsorTitle: this.form.sponsorTitle.trim(),
      sponsorEmail: this.form.sponsorEmail.trim(),
      sponsorPhone: this.form.sponsorPhone.trim() || undefined,
      agencyName: this.form.agencyName.trim(),
      agencyDomain: this.agencyDomain,
      swornOfficersCount: this.swornOfficerCount,
      officers: this.form.officers.map(normalizeOfficerRow),
      reseller: this.form.reseller.trim(),
      resellerPocName: this.form.resellerPocName.trim() || undefined,
      resellerPocEmail: this.form.resellerPocEmail.trim() || undefined,
      mdtPurchaseWindow: this.form.mdtPurchaseWindow || MdtPurchaseWindow.NotSure,
      mdtManufacturer: this.form.mdtManufacturer.trim(),
      mdtModel: this.form.mdtModel.trim(),
      bwcPlatform: this.form.bwcPlatform.trim(),
      rmsPlatform: this.form.rmsPlatform.trim(),
      acceptedTerms: this.form.acceptedTerms,
    };

    if (mode === 'draft')
      return base;

    return {
      ...base,
      sponsorPassword: this.form.sponsorPassword,
      ...(source ? { source } : {}),
    };
  }

  @action
  hydrateFromPayload(payload: unknown) {
    const draftPayload = parseDraftPayload(payload);
    if (!isDraftRecord(draftPayload))
      return;

    const next = createAgencySignupFormModel();
    const {
      officers = [],
      minOfficers = [],
      maxOfficers = [],
      ...draftFields
    } = draftPayload;
    Object.assign(next, draftFields);
    next.sponsorPassword = '';
    next.swornOfficersCount = String(draftPayload.swornOfficersCount || '');
    next.officers = [
      ...officers,
      ...minOfficers,
      ...maxOfficers,
    ].slice(0, MAX_PILOT_OFFICERS).map(mergeOfficerRow);
    this.form = next;
    this.persistLocalDraft();
  }

  private hydrateFromLocalStorage() {
    const raw = localStorage.getItem(AGENCY_SIGNUP_DRAFT_KEY);
    if (!raw)
      return;

    try {
      this.hydrateFromPayload(JSON.parse(raw));
    } catch {
      localStorage.removeItem(AGENCY_SIGNUP_DRAFT_KEY);
    }
  }

  private persistLocalDraft() {
    localStorage.setItem(AGENCY_SIGNUP_DRAFT_KEY, JSON.stringify(this.toApiPayload('draft')));
  }

  private clearSaveTimer() {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
  }
}

function resizeRows(rows: AgencyOfficerFormRow[], size: number): AgencyOfficerFormRow[] {
  return Array.from({ length: size }, (_item, index) => rows[index] ?? createEmptyOfficerRow());
}

function getRosterRowLabel(count: number): string {
  return `row${count === 1 ? '' : 's'}`;
}

function isEmailKey(key: keyof AgencyOfficerFormRow): boolean {
  return key === 'email' || key === 'supervisorEmail';
}

function normalizeOfficerRow(row: AgencyOfficerFormRow): AgencyOfficerFormRow {
  return {
    firstName: row.firstName.trim(),
    lastName: row.lastName.trim(),
    title: row.title.trim(),
    email: row.email.trim(),
    supervisorTitle: row.supervisorTitle.trim(),
    supervisorName: row.supervisorName.trim(),
    supervisorEmail: row.supervisorEmail.trim(),
  };
}

function mergeOfficerRow(row: Partial<AgencyOfficerFormRow>): AgencyOfficerFormRow {
  return {
    ...createEmptyOfficerRow(),
    ...row,
  };
}

function parseDraftPayload(payload: unknown): unknown {
  if (typeof payload !== 'string')
    return payload;

  try {
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

function isDraftRecord(value: unknown): value is Partial<AgencySignupFormModel> & {
  swornOfficersCount?: number | string;
  officers?: Partial<AgencyOfficerFormRow>[];
  minOfficers?: Partial<AgencyOfficerFormRow>[];
  maxOfficers?: Partial<AgencyOfficerFormRow>[];
} {
  return typeof value === 'object' && value !== null;
}
